import { useDispatch } from 'react-redux';
import axios from '../utils/axiosConfig';
import { errorHandler } from '../utils/errorHandler';
import { SMapAction } from '../redux/slices/strategyMap';
import toast from 'react-hot-toast';
import { ConfirmDeleteModal } from '../shared/modals/ConfirmDeleteModal';

export type LevelSmapDependencies = {
  dependency: {
    id: string;
    type: "project";
  }[];
  id: string;
  type: "roadmap";
};

export interface StrategyMapData {
  id: string;
  name: string;
  type: string;
  entity_type: string;
  child_entity_type: string;
  stage: string;
  status: string;
  description: string;
  start_date: string;
  end_date: string;
  progress: number;
  members: any[];
  costs: {
    total_cost: number;
    total_billing: number;
  };
  sim_data: {
    costs: {
      total_cost: number;
      total_billing: number;
    };
    end_date: string;
    start_date: string;
    status: string;
    progress: number;
  };
  integration_data: { [key: string]: any };
  dependencies: LevelSmapDependencies[];
}


type IntegrationItem = {
  integration_item_type: "feature" | "bug" | "task" | string;
  integration_status: "new" | "in_progress" | "done" | string;
  integration_id: string;
  integration_type: "azure" | "jira" | "linear" | string;
  integration_tags: {
    area_path: string;
    iteration_path: string;
    board_column: string;
    project_name: string;
    team_name: string;
  };
  integration_url: string;
};

export interface StrategyEntityItemType {
  id: string;
  name: string;
  description: string;
  stage: string;
  source: string;
  pinned?:boolean;
  type: string;
  entity_type: string;
  child_entity_type: string;
  child_entity_types: Record<string, number>;
  entity_ids: Array<{ [key: string]: any }>;
  entities: StrategyEntityItemType[];
  integration_data?: IntegrationItem;
  created_at: string;
  is_sim_pending: boolean;
  created_by: string;
  milestones: any[];
  dependencies: LevelSmapDependencies[];
  stats: {
    total: number;
    total_roadmaps: number;
    total_projects: number;
    on_track: number;
    not_started: number;
    completed: number;
    closed: number;
    at_risk: number;
  };
  status: string;
  progress: number;
  members: any[];
  start_date: string;
  end_date: string;
  modified_at: string;
  sim_data: {
    start_date: string;
    end_date: string;
    progress: number;
    status: string;
    stats: {
      total: number;
      total_roadmaps: number;
      total_projects: number;
      on_track: number;
      not_started: number;
      completed: number;
      closed: number;
      at_risk: number;
    };
    costs: {
      total_cost: number;
      total_billing: number;
    };
  };
  costs: {
    total_cost: number;
    total_billing: number;
  };
}

export type StrategyMapListItem = StrategyEntityItemType;

export interface EntityResponse {
  items: StrategyMapListItem[];
  total?: number;
}

export interface CreateSmapPayload {
  name: string;
  description: string;
  entities: Array<{
    id: string;
    type: string;
  }>;
  milestones: Array<{
    name: string;
    start_date: string;
    color: string;
  }>;
  dependencies: any[];
}

export interface UpdateSmapPayload {
  name?: string;
  description?: string;
  entities?: Array<{
    id: string;
    type: string;
  }>;
  milestones?: Array<{
    name: string;
    start_date: string;
    color: string;
  }>;
  dependencies?: any[];
}

export interface LevelSmapResponse {
  strategymaps: StrategyEntityItemType[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

export const useStrategyMapService = () => {
    const dispatch = useDispatch();

    const fetchLeveldSmap = async (
        page: number = 1,
        per_page: number = 10,
        search: string = '',
        search_type: string = 'smap',
        sort_map: string | null = null,
        source: string | null = null
    ) => {
        try {
            if (page === 1) {
                dispatch(SMapAction.setLoading(true));
            } else {
                dispatch(SMapAction.setLoadingMore(true));
            }

            const params: any = {
              page,
              per_page,
              search,
              search_type,
            };

            if (sort_map) params.sort_map = sort_map;
            if (source) params.source = source;

            const response = await axios.get('/strategymaps', { params });
            const data: LevelSmapResponse = response.data;

            if (page === 1) {
                dispatch(SMapAction.setSmaps(data.strategymaps));
            } else {
                dispatch(SMapAction.appendSmaps(data.strategymaps));
            }

            dispatch(SMapAction.setPagination({
                total: data.total,
                page: data.page,
                totalPages: data.pages
            }));

            return data;
        } catch (error) {
            dispatch(SMapAction.setError(error));
            errorHandler.showError(error);
            throw error;
        } finally {
            dispatch(SMapAction.setLoading(false));
            dispatch(SMapAction.setLoadingMore(false));
        }
    };

    const fetchSingleStrategyMap = async (strategymap_id: string, hardRefresh: boolean = false) => {
      try {
            const response = await axios.get<StrategyEntityItemType>(
              `/strategymap/${strategymap_id}`,
              {
                params: {
                  force_refresh:hardRefresh,
                },
              },
            );
            return response.data;
        } catch (error) {
            errorHandler.showError(error);
            throw error;
        } finally {
        }
    };

    const createStrategyMap = async (payload: CreateSmapPayload) => {
        try {
            dispatch(SMapAction.setLoading(true));
            const response = await axios.post('/strategymap', payload);
            return response.data;
        } catch (error) {
            errorHandler.showError(error);
            throw error;
        } finally {
            dispatch(SMapAction.setLoading(false));
        }
    };
    const updateStrategyMap = async (
      strategymap_id: string,
      payload: UpdateSmapPayload,
    ) => {
      try {
        dispatch(SMapAction.setLoading(true));
        const response = await axios.put(
          `/strategymap/${strategymap_id}`,
          payload,
        );
        return response.data;
      } catch (error) {
        errorHandler.showError(error);
        throw error;
      } finally {
        dispatch(SMapAction.setLoading(false));
      }
    };
    const deleteStrategyMap = async (strategymap_id: string) => {
        try {

            const confirmationDelete = await ConfirmDeleteModal("Strategy Map");
            if (!confirmationDelete) return;


            dispatch(SMapAction.setLoading(true));

            const response = await toast.promise(axios.delete(`/strategymap/${strategymap_id}`), {
                    loading: 'Deleting...',
                    success: 'Deleted successfully!',
                    error: 'Failed to delete.'
                  });
            return response.data;
        } catch (error) {
            errorHandler.showError(error);
            throw error;
        } finally {
            dispatch(SMapAction.setLoading(false));
        }
    };

    const fetchEntities = async (
        page: number = 1,
        per_page: number = 20,
        search: string = '',
        item_type: string = 'all',
        sort_by: string = 'modified_at_desc',
    ) => {
        try {
          let filterPayload:any= {
            item_type: item_type == "all" ? null : item_type,
          };
          if(item_type == "container_epic"){
              filterPayload = {
                search_type:"roadmap",
                item_type: "epic"
              }
          }
          if(item_type == "executing_epic"){
              filterPayload = {
                search_type:"project",
                item_type: "epic"
              }
          }
          if(item_type == "project"){
              filterPayload = {
                search_type:"project",
              }
          }
            const params = {
              page,
              per_page,
              search,
              sort_by,
              ...filterPayload,
            };
            const response = await axios.get('/organizations/roadmaps_and_projects', { params });
            return response.data as EntityResponse;
        } catch (error) {
            errorHandler.showError(error);
            throw error;
        }
    };

    const resetStrategyMaps = ()=>{
          dispatch(SMapAction.resetState());

    }
    const fetchEntitiesByIds = async (roadmapIds: string[] = [], projectIds: string[] = []) => {
      try {
        const params = new URLSearchParams();
        if (roadmapIds.length > 0) params.append('roadmap_ids', roadmapIds.join(','));
        if (projectIds.length > 0) params.append('project_ids', projectIds.join(','));

        const response = await axios.get('/organizations/roadmaps_and_projects/by_ids', { params });
        return response.data;
      } catch (error) {
        errorHandler.showError(error);
        throw error;
      }
    };

    return { fetchLeveldSmap, createStrategyMap, updateStrategyMap, fetchEntities,  resetStrategyMaps, deleteStrategyMap, fetchSingleStrategyMap, fetchEntitiesByIds};

};
